import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, MenuButton, Segmented, Select, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionClient,
  type ProductStructure,
  BOM_STATUSES,
  BomClient,
  BomLineClient,
  BomOperationClient,
  formatNumber,
  formatSquareMetres,
  refusalMessage,
  revisionLabel,
  VirtualPartOptionClient,
  type BomDTO,
  type BomExplosionLineDTO,
  type BomLineDTO,
  type BomOperationDTO,
  type DepartmentDTO,
  type OperationDTO,
  type PartDTO,
  type PartGridDTO,
  type UnitDTO,
  type VirtualPartOptionDTO,
  BOM_SUPPLIES,
  type BomSupply,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { bomSupplyLabel } from "./bomSupply";
import { OpenRecord } from "../../components/OpenRecord";
import { fieldErrors, numberError, optionalNumber, requiredNumber } from "./PartRelatedTabs";
import { PackingPanel, PackingSummary, usePacking } from "./PackingPanel";
import { PartPicker } from "./PartPicker";
import { DerivedRoutingForNewPart, RoutingStepsPanel } from "./RoutingStepsPanel";

/**
 * A line as the tree shows it: where it sits (depth, and a path unique even
 * when the same sub-assembly appears twice).
 */
type BomRow = BomLineDTO & { depth: number; path: string };

/** Stable, so useQueries hands back the same array until an answer changes. */
const optionData = (results: Array<{ data?: VirtualPartOptionDTO[] | undefined }>) => results.map((result) => result.data);

/** A part type in words; a virtual panel is neither bought nor made. */
function componentTypeLabel(type: string | null | undefined, t: (key: string, fallback: string) => string): string {
  switch (type) {
    case "MANUFACTURED":
      return t("part.type.manufactured", "Made");
    case "VIRTUAL":
      return t("part.type.virtual", "Virtual part");
    default:
      return t("part.type.purchased", "Bought");
  }
}

/** A revision's status in words, for badges, pickers and filters. */
export function bomStatusLabel(status: string, t: (key: string, fallback: string) => string): string {
  switch (status) {
    case "DRAFT":
      return t("bom.status.draft", "Draft");
    case "ACTIVE":
      return t("bom.status.active", "Active");
    case "ARCHIVED":
      return t("bom.status.archived", "Archived");
    default:
      return status;
  }
}

/** A revision as people name it: when it was made. */
const labelOf = (bom: BomDTO) => revisionLabel(bom.created_at, bom.revision);

/** Deep enough for any real product; stops a BOM that contains itself. */
const MAX_TREE_DEPTH = 12;

/**
 * How this part is made.
 *
 * <p>Two views of the same tree, because they answer different questions. The
 * tree is the editable one: this revision's own lines, and under any made
 * component a chevron that opens that component's active BOM in place, level
 * by level, fetched as it is opened. Lines at every level edit inline — a sub
 * level is that component's own BOM, and the screen says so — so fixing a
 * quantity two levels down does not mean leaving this part. Rows do not
 * navigate when clicked; the arrow at the end of a row opens the component
 * on its own BOM tab, on purpose. The explosion is read-only and computed by
 * the server — walking all levels in the browser would mean holding all
 * 67,812 lines to answer a question about twenty of them.</p>
 *
 * <p>Every revision is written for one unit; the build enforces it, so there
 * is no base quantity to show or edit. A virtual panel line (a VIRTUAL
 * component, consumed by area) reads in m² with its cut size, and names the
 * real panel it is cut from, picked among the virtual panel's options. Lines
 * the engineering BOM build wrote say so: the next run rewrites them.</p>
 *
 * <p>Revisions are a picker rather than a list page. A part has one or two of
 * them and the interesting one is almost always ACTIVE; a screen of its own
 * would be a click between a part and the thing people came to see.</p>
 */
export function PartBomTab({
  part,
  units,
  operations,
  departments,
  readOnly,
  showRouting,
}: {
  part: PartDTO;
  units: UnitDTO[];
  operations: OperationDTO[];
  departments: DepartmentDTO[];
  readOnly: boolean;
  /**
   * The routing and labour standards under the lines. Default: when the part
   * has a routing (0072). The product's Fabrication and Assembly tabs show the
   * bill alone.
   */
  showRouting?: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const boms = useMemo(() => new BomClient(http), [http]);
  const lineClient = useMemo(() => new BomLineClient(http), [http]);
  const operationClient = useMemo(() => new BomOperationClient(http), [http]);

  const [view, setView] = useState<"single" | "exploded">("single");
  // The floor's process routing by default; SIGM's labour standards a click away.
  const [lowerView, setLowerView] = useState<"routing" | "labour">("routing");
  const routed = showRouting ?? part.has_routing === true;
  const [explodeQuantity, setExplodeQuantity] = useState("1");
  const [selectedBomId, setSelectedBomId] = useState<number | null>(null);

  // The whole revision list for this part — two or three rows, and the picker
  // needs all of them anyway.
  const { data: revisions } = useQuery({
    queryKey: ["manufacturing", "bom", "part", part.id],
    queryFn: async () => (await boms.find({ size: 100, filters: { part_id: [String(part.id)] } })).content,
    staleTime: 60_000,
  });

  const current: BomDTO | undefined = useMemo(() => {
    const list = revisions ?? [];
    if (selectedBomId !== null) return list.find((row) => row.id === selectedBomId);
    return list.find((row) => row.status === "ACTIVE") ?? list[0];
  }, [revisions, selectedBomId]);

  const { data: lines } = useQuery({
    queryKey: ["manufacturing", "bom-line", current?.id ?? 0],
    queryFn: async () => (await lineClient.find({ size: 1000, filters: { bom_id: [String(current!.id)] } })).content,
    enabled: current !== undefined,
    staleTime: 60_000,
  });

  const { data: steps } = useQuery({
    queryKey: ["manufacturing", "bom-operation", current?.id ?? 0],
    queryFn: async () => (await operationClient.find({ size: 1000, filters: { bom_id: [String(current!.id)] } })).content,
    enabled: current !== undefined,
    staleTime: 60_000,
  });

  // ── the tree ────────────────────────────────────────────────────────────
  // Which component rows are open (by path), each component's ACTIVE revision
  // (null: it has none, so no chevron), and the lines of every revision opened
  // so far. Looked up in batches as rows appear, never all 14,000 at once.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [activeBoms, setActiveBoms] = useState<Map<number, BomDTO | null>>(new Map());
  const [childLines, setChildLines] = useState<Map<number, BomLineDTO[]>>(new Map());

  useEffect(() => {
    setOpen(new Set());
  }, [current?.id]);

  const seenComponents = useMemo(() => {
    const ids = new Set<number>();
    for (const line of lines ?? []) if (line.component_part_id !== null) ids.add(line.component_part_id);
    for (const list of childLines.values()) for (const line of list) if (line.component_part_id !== null) ids.add(line.component_part_id);
    return [...ids];
  }, [lines, childLines]);

  useEffect(() => {
    const unknown = seenComponents.filter((id) => !activeBoms.has(id));
    if (unknown.length === 0) return;
    let cancelled = false;
    void (async () => {
      const found = new Map<number, BomDTO | null>(unknown.map((id) => [id, null]));
      for (let i = 0; i < unknown.length; i += 200) {
        const chunk = unknown.slice(i, i + 200).map(String);
        const page = await boms.find({ size: 1000, filters: { part_id: chunk, status: ["ACTIVE"] } });
        for (const bom of page.content) found.set(bom.part_id, bom);
      }
      if (!cancelled) setActiveBoms((previous) => new Map([...previous, ...found]));
    })();
    return () => {
      cancelled = true;
    };
  }, [seenComponents, activeBoms, boms]);

  const loadChildLines = useCallback(
    async (bomId: number) => {
      const page = await lineClient.find({ size: 1000, filters: { bom_id: [String(bomId)] } });
      setChildLines((previous) => new Map(previous).set(bomId, page.content));
    },
    [lineClient],
  );

  const toggle = useCallback(
    (path: string, bomId: number) => {
      setOpen((previous) => {
        const next = new Set(previous);
        if (next.has(path)) {
          for (const key of [...next]) if (key === path || key.startsWith(`${path}/`)) next.delete(key);
        } else {
          next.add(path);
        }
        return next;
      });
      if (!childLines.has(bomId)) void loadChildLines(bomId);
    },
    [childLines, loadChildLines],
  );

  const rows: BomRow[] = useMemo(() => {
    const out: BomRow[] = [];
    const walk = (list: BomLineDTO[], depth: number, prefix: string, ancestors: Set<number>) => {
      for (const line of list) {
        const path = `${prefix}/${String(line.id)}`;
        out.push({ ...line, depth, path });
        const component = line.component_part_id;
        if (component === null || !open.has(path) || depth >= MAX_TREE_DEPTH || ancestors.has(component)) continue;
        const bom = activeBoms.get(component);
        const children = bom ? childLines.get(bom.id) : undefined;
        if (bom && children) walk(children, depth + 1, path, new Set([...ancestors, component]));
      }
    };
    walk(lines ?? [], 0, "", new Set([part.id]));
    return out;
  }, [lines, open, activeBoms, childLines, part.id]);
  const nested = rows.some((row) => row.depth > 0);

  // ── boxes ───────────────────────────────────────────────────────────────
  // A version's output part is its assembly BOM (0066), read a box at a time:
  // each box's lines, and the pieces of the semi-finished packed in it (0068).
  // The lines in no box (the semi-finished x1 itself) show with All lines. A
  // line's children go with it.
  const { data: structure } = useQuery({
    queryKey: ["manufacturing", "product-structure", "part", part.id],
    queryFn: () => new AssemblyVersionClient(http).ofPart(part.id),
    staleTime: 60_000,
  });
  const version = structure?.versions.find((row) => row.output_part_id === part.id) ?? null;
  const sections = useMemo(() => {
    if (!version) return null;
    const byBox = new Map<number | null, BomRow[]>();
    let box: number | null = null;
    for (const row of rows) {
      if (row.depth === 0) box = row.box_number ?? null;
      byBox.set(box, [...(byBox.get(box) ?? []), row]);
    }
    const last = Math.max(version.box_count ?? 1, ...rows.map((row) => row.box_number ?? 0));
    return Array.from({ length: Math.max(1, last) }, (_, index) => index + 1).map((number) => ({
      box: number,
      rows: byBox.get(number) ?? [],
    }));
  }, [version, rows]);
  const [adding, setAdding] = useState<{ box: number | null } | null>(null);
  // One box at a time, at the full size of the block; "all" is the whole
  // bill with its Box column.
  const [boxView, setBoxView] = useState<"all" | number>(1);
  const shownBox = sections && typeof boxView === "number" && boxView > sections.length ? 1 : boxView;
  const shownSection = sections?.find((section) => section.box === shownBox) ?? null;
  const packing = usePacking(version, structure?.semi_finished_part_id ?? null, sections?.length ?? 0);

  // ── virtual panels ──────────────────────────────────────────────────────
  // The real panels each virtual panel on screen can be cut from: one small
  // request per virtual panel (a BOM names two or three), not per line.
  const optionClient = useMemo(() => new VirtualPartOptionClient(http), [http]);
  const virtualIds = useMemo(
    () =>
      [...new Set(rows.filter((row) => row.component_part_type === "VIRTUAL" && row.component_part_id !== null).map((row) => row.component_part_id!))].sort(
        (a, b) => a - b,
      ),
    [rows],
  );
  const optionLists = useQueries({
    queries: virtualIds.map((id) => ({
      queryKey: ["manufacturing", "virtual-part-option", id],
      queryFn: () => optionClient.forVirtualPart(id),
      staleTime: 60_000,
    })),
    combine: optionData,
  });
  const optionsByVirtual = useMemo(() => {
    const map = new Map<number, VirtualPartOptionDTO[]>();
    virtualIds.forEach((id, index) => {
      const list = optionLists[index];
      if (list) map.set(id, list);
    });
    return map;
  }, [virtualIds, optionLists]);

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const warehouseById = useMemo(() => new Map((warehouses.data ?? []).map((w) => [w.id, w])), [warehouses.data]);
  const consumptionOptions = (warehouses.data ?? [])
    .filter((w) => w.is_active && w.kind !== "DISTRIBUTION")
    .map((w) => ({ value: String(w.id), label: w.code }));

  const quantity = Number(explodeQuantity);
  const validQuantity = Number.isFinite(quantity) && quantity > 0;

  const { data: exploded, isFetching: exploding } = useQuery({
    queryKey: ["manufacturing", "bom", "explode", part.id, validQuantity ? quantity : 1],
    queryFn: () => boms.explode(part.id, validQuantity ? quantity : 1),
    enabled: view === "exploded",
    staleTime: 60_000,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-line"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-operation"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-routing-step"] });
    // Opened sub-levels are fetched outside the query cache: refetch them.
    await Promise.all([...childLines.keys()].map((bomId) => loadChildLines(bomId)));
  };

  // A new revision usually changes a few lines of the current one, so the
  // default is a copy; an empty one is still there for a fresh start.
  const copyRevision = useMutation({
    mutationFn: (source: BomDTO) => boms.copy(source.id),
    onSuccess: async (created) => {
      await refresh();
      setSelectedBomId(created.id);
      toast.success(t("bom.revision.created", "Revision {{n}} created", { n: labelOf(created) }));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Something went wrong.")),
  });

  const createRevision = useMutation({
    mutationFn: async () => {
      const next = Math.max(0, ...(revisions ?? []).map((row) => row.revision)) + 1;
      return boms.create({
        id: 0,
        part_id: part.id,
        revision: next,
        // DRAFT, always. A new revision that went straight to ACTIVE would
        // retire the current one before anyone had put a line in it.
        status: "DRAFT",
        name: null,
        date_from: null,
        date_to: null,
        note: null,
        // Written for one, the nexpim way. SIGM's batches of 100 stay on the
        // revisions it wrote.
        base_quantity: 1,
      });
    },
    onSuccess: async (created) => {
      await refresh();
      setSelectedBomId(created.id);
      toast.success(t("bom.revision.created", "Revision {{n}} created", { n: labelOf(created) }));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Something went wrong.")),
  });

  // Activating is its own operation, because it retires the revision that was
  // active. Every other status change is a plain edit.
  const setStatus = useMutation({
    mutationFn: async (status: BomDTO["status"]) => {
      if (status === "ACTIVE") {
        await boms.activate(current!.id);
      } else {
        await boms.update({ ...current!, status });
      }
    },
    onSuccess: async () => {
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Something went wrong.")),
  });

  const removeDraft = useMutation({
    mutationFn: () => boms.remove(current!.id),
    onSuccess: async () => {
      setSelectedBomId(null);
      await refresh();
      toast.success(t("bom.revision.deleted", "Draft deleted"));
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Something went wrong.")),
  });

  const unitOptions = units
    .filter((unit) => unit.is_active)
    .map((unit) => ({ value: String(unit.id), label: unit.code }));
  const operationOptions = operations
    .filter((operation) => operation.is_active)
    .map((operation) => ({ value: String(operation.id), label: `${operation.code} · ${operation.name}` }));

  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);
  const operationById = useMemo(() => new Map(operations.map((o) => [o.id, o])), [operations]);
  const departmentById = useMemo(() => new Map(departments.map((d) => [d.id, d])), [departments]);

  // Saved at once, like a toggle: the panel a line is cut from is one choice,
  // not an edit worth opening the row for. 409 when it is not an option.
  const saveCutFrom = useCallback(
    async (row: BomRow, realPartId: number | null) => {
      const { depth: _depth, path: _path, ...line } = row;
      try {
        await lineClient.update({ ...line, default_real_part_id: realPartId });
      } catch (failure) {
        toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "bom-line"] });
      if (childLines.has(row.bom_id)) await loadChildLines(row.bom_id);
      toast.success(t("notification.saved", "Saved"));
    },
    [lineClient, queryClient, childLines, loadChildLines, t],
  );

  const hasBoxes = rows.some((row) => row.box_number != null);
  const lineCols = useMemo(() => {
    const c = defineColumns<BomRow>();
    return [
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", priority: 4 }),
      c.custom(
        "component",
        (row) => row.component_part_number ?? "",
        (row) => {
          const bom = row.component_part_id === null ? undefined : activeBoms.get(row.component_part_id);
          const isOpen = open.has(row.path);
          return (
            <span className="flex min-w-0 items-center gap-1" style={{ paddingLeft: `${String(row.depth * 20)}px` }}>
              {bom ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(row.path, bom.id);
                  }}
                  className="flex size-6 shrink-0 items-center justify-center rounded text-ink-muted hover:bg-surface-sunk hover:text-ink"
                  aria-expanded={isOpen}
                  title={isOpen ? t("bom.tree.collapse", "Hide its components") : t("bom.tree.expand", "Show its components")}
                >
                  {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                </button>
              ) : (
                <span className="size-6 shrink-0" />
              )}
              {row.kind === "TEXT" ? (
                <span className="truncate text-ink-muted italic">{row.note}</span>
              ) : (
                <span className="truncate">
                  <span className={bom ? "font-medium" : ""}>{row.component_part_number}</span>
                  <span className="ml-2 text-[11px] text-ink-faint">{row.component_part_name}</span>
                </span>
              )}
              {row.is_generated && (
                <span
                  className="ml-1 shrink-0 rounded border border-line px-1 text-[10.5px] text-ink-faint"
                  title={t("bom.line.generated.title", "Written by the engineering BOM build; rerunning it rewrites this line.")}
                >
                  {t("bom.line.generated", "Generated")}
                </span>
              )}
            </span>
          );
        },
        { headerKey: "column.bom.component", priority: 1 },
      ),
      c.custom(
        "quantity",
        (row) =>
          row.kind === "TEXT" ? "" : row.component_part_type === "VIRTUAL" ? formatSquareMetres(row.quantity) : formatNumber(row.quantity),
        (row) =>
          row.kind === "TEXT" ? (
            <span />
          ) : row.component_part_type === "VIRTUAL" ? (
            // Stored in mm², read in m²; the note carries the cut size, from the part's sizes.
            <span
              className="tabular-nums"
              title={
                row.is_sized
                  ? t("bom.sized.virtual.title", "{{n}} mm², from the part's length and width: change those, not this.", { n: formatNumber(row.quantity) })
                  : t("bom.virtual.quantity-mm2", "{{n}} mm²", { n: formatNumber(row.quantity) })
              }
            >
              {t("bom.virtual.quantity", "{{n}} m²", { n: formatSquareMetres(row.quantity) })}
              {row.note && <span className="ml-2 text-[11px] text-ink-faint">{row.note}</span>}
            </span>
          ) : row.is_sized ? (
            // An edge band: the length of the edges it covers.
            <span className="tabular-nums" title={t("bom.sized.title", "From the part's sizes and edges: change those, not this.")}>
              {formatNumber(row.quantity)}
              {row.note && <span className="ml-2 text-[11px] text-ink-faint">{row.note}</span>}
            </span>
          ) : (
            <span className="tabular-nums">{formatNumber(row.quantity)}</span>
          ),
        { headerKey: "column.bom.quantity", priority: 2 },
      ),
      c.custom(
        "cut_from",
        (row) => row.default_real_part_number ?? "",
        (row) =>
          row.component_part_type !== "VIRTUAL" || row.component_part_id === null ? (
            <span />
          ) : (
            <CutFrom
              row={row}
              options={optionsByVirtual.get(row.component_part_id)}
              editable={!readOnly}
              onSave={saveCutFrom}
            />
          ),
        { headerKey: "column.bom.cut-from", priority: 3 },
      ),
      c.text("unit", (row) => (row.unit_id === null ? "" : (unitById.get(row.unit_id)?.code ?? "")), {
        headerKey: "column.bom.unit",
        priority: 3,
      }),
      c.text("operation", (row) => (row.operation_id === null ? "" : (operationById.get(row.operation_id)?.code ?? "")), {
        headerKey: "column.bom.operation",
        filter: "select",
      }),
      c.text("scrap", (row) => (row.kind === "TEXT" ? "" : formatNumber(row.scrap_percent)), {
        headerKey: "column.bom.scrap",
        defaultVisible: false,
      }),
      c.text(
        "consumption_warehouse",
        (row) => (row.consumption_warehouse_id === null ? "" : (warehouseById.get(row.consumption_warehouse_id)?.code ?? "")),
        { headerKey: "column.bom.consumption-warehouse", filter: "select", defaultVisible: false },
      ),
      // Assembly BOMs only: the box the line goes in. Shown by default where any line has one.
      c.text("box", (row) => (row.box_number == null ? "" : String(row.box_number)), {
        headerKey: "column.bom.box",
        filter: "select",
        defaultVisible: hasBoxes && (!version || shownBox === "all"),
      }),
      // Blank: as the component says. Set only when this parent gets it another way.
      c.text("supply", (row) => (row.supply ? bomSupplyLabel(row.supply, t) : ""), {
        header: t("column.bom.supply", "Supply override"),
        filter: "select",
        defaultVisible: false,
      }),
    ];
  }, [unitById, operationById, warehouseById, activeBoms, open, toggle, optionsByVirtual, readOnly, saveCutFrom, hasBoxes, version, shownBox, t]);

  const stepCols = useMemo(() => {
    const c = defineColumns<BomOperationDTO>();
    return [
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", priority: 4 }),
      c.custom(
        "operation",
        (row) => operationById.get(row.operation_id)?.code ?? "",
        (row) => (
          <span>
            {operationById.get(row.operation_id)?.code}
            <span className="ml-2 text-[11px] text-ink-faint">{operationById.get(row.operation_id)?.name}</span>
          </span>
        ),
        { headerKey: "column.bom.operation", priority: 1 },
      ),
      c.text("hours", (row) => formatNumber(row.hours), { headerKey: "column.bom.hours", priority: 2 }),
      c.text("crew_size", (row) => formatNumber(row.crew_size), { headerKey: "column.bom.crew", priority: 3 }),
      c.custom(
        "is_setup",
        (row) => (row.is_setup ? t("common.yes", "Yes") : t("common.no", "No")),
        (row) =>
          row.is_setup ? (
            <Badge variant="neutral" size="sm">
              {t("bom.setup", "Setup")}
            </Badge>
          ) : (
            <span />
          ),
        { headerKey: "column.bom.setup", filter: "select" },
      ),
      c.text(
        "department",
        (row) =>
          row.department_id === null
            ? (departmentById.get(operationById.get(row.operation_id)?.department_id ?? -1)?.code ?? "")
            : (departmentById.get(row.department_id)?.code ?? ""),
        { headerKey: "column.bom.department", filter: "select", defaultVisible: false },
      ),
    ];
  }, [operationById, departmentById, t]);

  const explodedCols = useMemo(() => {
    const c = defineColumns<BomExplosionLineDTO>();
    return [
      c.custom(
        "part",
        (row) => row.part_number,
        (row) => (
          // Indentation is the tree. The server already ordered by path, so the
          // level is all the client needs to draw the shape.
          <span style={{ paddingLeft: `${(row.level - 1) * 18}px` }}>
            <span className={row.has_children ? "font-medium" : ""}>{row.part_number}</span>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
          </span>
        ),
        { headerKey: "column.bom.component", priority: 1 },
      ),
      c.number("level", (row) => row.level, { headerKey: "column.bom.level", defaultVisible: false }),
      c.text(
        "quantity",
        (row) => (row.part_type === "VIRTUAL" ? formatSquareMetres(row.quantity) : formatNumber(row.quantity)),
        { headerKey: "column.bom.required", priority: 2 },
      ),
      c.text("unit", (row) => (row.part_type === "VIRTUAL" ? "m²" : (row.unit_code ?? "")), { headerKey: "column.bom.unit", priority: 3 }),
      c.custom(
        "type",
        (row) => componentTypeLabel(row.part_type, t),
        (row) => (
          <Badge variant={row.part_type === "MANUFACTURED" ? "info" : row.part_type === "VIRTUAL" ? "warning" : "neutral"} size="sm">
            {componentTypeLabel(row.part_type, t)}
          </Badge>
        ),
        { headerKey: "column.part.type", filter: "select" },
      ),
      c.text("parent", (row) => row.parent_part_number, { headerKey: "column.bom.parent", defaultVisible: false }),
      c.text("operation", (row) => row.operation_code ?? "", { headerKey: "column.bom.operation", defaultVisible: false }),
    ];
  }, [t]);

  if (!revisions) {
    return <div className="p-4 text-[13px] text-ink-muted lg:p-6">{t("common.loading", "Loading…")}</div>;
  }

  if (revisions.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 p-4 lg:p-6">
        <p className="text-[13px] text-ink-muted">
          {t(
            "bom.none",
            "This part has no bill of materials. 13,169 of the 27,298 imported parts have none, and 5,439 of those are marked as made in-house: SIGM holds nothing for them either.",
          )}
        </p>
        {!readOnly && (
          <Button variant="primary" loading={createRevision.isPending} onClick={() => createRevision.mutate()}>
            {t("bom.create-first", "Create the first revision")}
          </Button>
        )}
        {routed && (
          <div className="-mx-4 w-full border-t border-line pt-2 lg:-mx-6">
            <DerivedRoutingForNewPart partId={part.id} readOnly={readOnly} onCreated={refresh} />
          </div>
        )}
      </div>
    );
  }

  // The box switcher's choices, each with its line count.
  const boxOptions = sections
    ? [
        ...sections.map((section) => ({
          value: String(section.box),
          label: t("bom.section.box", "Box {{n}}", { n: section.box }),
        })),
        { value: "all", label: t("bom.section.all", "All lines") },
      ]
    : [];

  // One table of lines: the whole revision, or one box of an assembly BOM.
  const linesTable = (data: BomRow[], key: string, empty: string, lead?: ReactNode, extra?: ReactNode) => (
    <DataTable<BomRow>
      key={key}
      data={data}
      toolbarLead={lead}
      toolbarExtra={extra}
      columns={lineCols}
      getRowId={(row) => row.path}
      rowClassName={(row) => (row.depth > 0 ? "bg-surface-sunk/40" : undefined)}
      editor={
        readOnly || !current
          ? undefined
          : {
              fields: {
                position: {
                  kind: "number",
                  get: (row) => row.position,
                  set: (row, value) => ({ ...row, position: requiredNumber(value) }),
                  required: true,
                },
                // A sized line (a panel, an edge band) follows the part's sizes (0078).
                quantity: {
                  kind: "number",
                  get: (row) => row.quantity,
                  set: (row, value) => ({ ...row, quantity: requiredNumber(value) }),
                  readOnly: (row) => Boolean(row.is_sized),
                },
                unit: {
                  readOnly: (row) => Boolean(row.is_sized),
                  kind: "select",
                  get: (row) => (row.unit_id === null ? "" : String(row.unit_id)),
                  set: (row, value) => ({ ...row, unit_id: value === "" ? null : Number(value) }),
                  options: unitOptions,
                },
                operation: {
                  kind: "select",
                  get: (row) => (row.operation_id === null ? "" : String(row.operation_id)),
                  set: (row, value) => ({ ...row, operation_id: value === "" ? null : Number(value) }),
                  options: operationOptions,
                },
                scrap: {
                  kind: "number",
                  get: (row) => row.scrap_percent,
                  set: (row, value) => ({ ...row, scrap_percent: requiredNumber(value) }),
                },
                // Blank: the part's own consumption warehouse, else
                // wherever the production order runs.
                consumption_warehouse: {
                  kind: "select",
                  get: (row) => (row.consumption_warehouse_id === null ? "" : String(row.consumption_warehouse_id)),
                  set: (row, value) => ({ ...row, consumption_warehouse_id: value === "" ? null : Number(value) }),
                  options: consumptionOptions,
                },
                box: {
                  kind: "number",
                  get: (row) => row.box_number ?? "",
                  set: (row, value) => ({ ...row, box_number: optionalNumber(value) }),
                },
                supply: {
                  kind: "select",
                  get: (row) => row.supply ?? "",
                  set: (row, value) => ({ ...row, supply: value === "" ? null : (value as BomSupply) }),
                  options: [
                    { value: "", label: t("bom.supply.as-component", "As the component says") },
                    ...BOM_SUPPLIES.map((supply) => ({ value: supply, label: bomSupplyLabel(supply, t) })),
                  ],
                },
              },
              // No add row: a component is a part out of 27,298, a
              // search and not a dropdown. Adding goes through
              // AddComponentDialog and its PartPicker.
              validate: (row) =>
                fieldErrors({
                  position: numberError(row.position, "whole", t),
                  box: row.box_number == null || (Number.isInteger(row.box_number) && row.box_number >= 1) ? undefined : t("bom.box.range", "A box number: 1, 2, 3."),
                  quantity: row.kind === "TEXT" ? undefined : numberError(row.quantity, "not-negative", t),
                  scrap:
                    row.kind === "TEXT"
                      ? undefined
                      : (numberError(row.scrap_percent, "not-negative", t) ??
                        (row.scrap_percent < 100 ? undefined : t("bom.scrap.range", "Under 100%."))),
                }),
              onSave: async (row) => {
                const { depth: _depth, path: _path, ...line } = row;
                // The cut-from select saves on its own; a draft opened
                // before it changed must not put the old panel back.
                const latest = rows.find((one) => one.id === row.id);
                await lineClient.update({ ...line, default_real_part_id: latest?.default_real_part_id ?? line.default_real_part_id ?? null });
                await refresh();
                toast.success(t("notification.saved", "Saved"));
              },
              onDelete: async (row) => {
                await lineClient.remove(row.id);
                await refresh();
                toast.success(t("notification.deleted", "Deleted"));
              },
              deleteConfirm: (row) =>
                row.depth > 0
                  ? {
                      title: t("bom.line.delete", "Remove this line?"),
                      message: t("bom.line.delete.nested", "It belongs to a component's own BOM: every product using that component loses it."),
                    }
                  : { title: t("bom.line.delete", "Remove this line?") },
              rowActions: (row) =>
                row.component_part_id === null ? null : (
                  <OpenRecord
                    target={{
                      kind: "part",
                      id: row.component_part_id,
                      ...(activeBoms.get(row.component_part_id) ? { tab: "bom" } : {}),
                    }}
                    label={row.component_part_number ?? ""}
                  />
                ),
            }
      }
      {...(readOnly || !current
        ? {
            rowActions: (row: BomRow) =>
              row.component_part_id === null ? null : (
                <OpenRecord
                  target={{
                    kind: "part",
                    id: row.component_part_id,
                    ...(activeBoms.get(row.component_part_id) ? { tab: "bom" } : {}),
                  }}
                  label={row.component_part_number ?? ""}
                />
              ),
          }
        : {})}
      emptyMessage={empty}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <BomRoleStrip partId={part.id} structure={structure ?? null} />
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2 lg:px-6">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "single", label: t("bom.view.tree", "Tree") },
            { value: "exploded", label: t("bom.view.exploded", "All levels") },
          ]}
        />

        {view === "single" ? (
          <>
            <Select
              value={String(current?.id ?? "")}
              options={revisions.map((row) => ({
                value: String(row.id),
                label: row.name
                  ? t("bom.revision.label-named", "{{n}}, {{status}}: {{name}}", { n: labelOf(row), status: bomStatusLabel(row.status, t), name: row.name })
                  : t("bom.revision.label", "{{n}}, {{status}}", { n: labelOf(row), status: bomStatusLabel(row.status, t) }),
              }))}
              onChange={(event) => setSelectedBomId(Number(event.target.value))}
              className="h-8 w-72"
            />
            {current && readOnly && (
              <Badge variant={current.status === "ACTIVE" ? "success" : current.status === "DRAFT" ? "warning" : "neutral"} size="sm">
                {bomStatusLabel(current.status, t)}
              </Badge>
            )}
            {current && (
              <Badge variant="neutral" size="sm">
                {t("bom.per-unit", "Quantities are for one unit")}
              </Badge>
            )}
            {!readOnly && current && (
              <>
                <Select
                  value={current.status}
                  aria-label={t("column.status", "Status")}
                  disabled={setStatus.isPending}
                  options={BOM_STATUSES.map((status) => ({ value: status, label: bomStatusLabel(status, t) }))}
                  onChange={async (event) => {
                    const status = event.target.value as BomDTO["status"];
                    // Activating retires the active revision and archiving the
                    // active one leaves the part with none: both change what
                    // production orders are built from, so both are asked.
                    const active = revisions.find((row) => row.status === "ACTIVE" && row.id !== current.id);
                    const question =
                      status === "ACTIVE"
                        ? {
                            title: t("bom.activate.title", "Make revision {{n}} the active one?", { n: labelOf(current) }),
                            message: active
                              ? t("bom.activate.message", "Revision {{old}} is archived. New production orders use revision {{n}}.", {
                                  old: labelOf(active),
                                  n: labelOf(current),
                                })
                              : t("bom.activate.message-first", "New production orders use revision {{n}}.", { n: labelOf(current) }),
                            confirmLabel: t("button.activate", "Activate"),
                          }
                        : current.status === "ACTIVE"
                          ? {
                              title: t("bom.deactivate.title", "Leave this part without an active revision?"),
                              message: t("bom.deactivate.message", "Production orders cannot be built from it until a revision is activated again."),
                              confirmLabel: t("bom.deactivate.confirm", "Change status"),
                              variant: "danger" as const,
                            }
                          : null;
                    if (question && !(await confirm(question))) return;
                    setStatus.mutate(status);
                  }}
                  className="h-8 w-32"
                />
                {!sections && (
                  <Button variant="ghost" onClick={() => setAdding({ box: null })}>
                    {t("bom.add-component", "Add a component")}
                  </Button>
                )}
                <MenuButton
                  variant="ghost"
                  label={t("bom.new-revision", "New revision")}
                  disabled={createRevision.isPending || copyRevision.isPending}
                  items={[
                    {
                      label: t("bom.new-revision.copy", "Copy of revision {{n}}", { n: labelOf(current) }),
                      onClick: () => copyRevision.mutate(current),
                    },
                    {
                      label: t("bom.new-revision.empty", "Empty revision"),
                      onClick: () => createRevision.mutate(),
                    },
                  ]}
                />
                {current.status === "DRAFT" && (
                  <Button
                    variant="ghost"
                    loading={removeDraft.isPending}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: t("bom.revision.delete", "Delete this draft?"),
                          message: t("bom.revision.delete.message", "Revision {{n}} and its lines and steps are deleted. This cannot be undone.", {
                            n: labelOf(current),
                          }),
                          confirmLabel: t("bom.revision.delete.confirm", "Delete draft"),
                          variant: "danger",
                        })
                      ) {
                        removeDraft.mutate();
                      }
                    }}
                  >
                    {t("bom.revision.delete.confirm", "Delete draft")}
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <label className="text-[12px] text-ink-muted" htmlFor="bom-explode-qty">
              {t("bom.explode.quantity", "Build quantity")}
            </label>
            <Input
              id="bom-explode-qty"
              type="number"
              min="1"
              value={explodeQuantity}
              onChange={(event) => setExplodeQuantity(event.target.value)}
              className="h-8 w-24"
              invalid={!validQuantity}
            />
            {exploding && <span className="text-[12px] text-ink-faint">{t("common.loading", "Loading…")}</span>}
            {exploded && (
              <Badge variant="neutral" size="sm">
                {t("bom.explode.count", "{{n}} requirements", { n: exploded.length })}
              </Badge>
            )}
          </>
        )}
      </div>

      {view === "single" && nested && (
        <div className="border-b border-line bg-info-soft px-4 py-1.5 text-[12px] text-info lg:px-6">
          {t(
            "bom.tree.nested-note",
            "Shaded lines belong to a component's own BOM. Editing them changes that component everywhere it is used.",
          )}
        </div>
      )}

      {view === "exploded" ? (
        <DataTable
          data={exploded ?? []}
          columns={explodedCols}
          getRowId={(row) => row.path}
          rowActions={(row) => (
            <OpenRecord target={{ kind: "part", id: row.part_id, ...(row.has_children ? { tab: "bom" } : {}) }} label={row.part_number} />
          )}
          emptyMessage={t("bom.explode.empty", "Nothing to build: this part's active revision has no components.")}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Each table is a flex column with its own scroll: without the
              flex, DataTable grows to its full height and spills over the
              routing below it. Components get the larger share. */}
          <div className="flex min-h-48 flex-[3] flex-col">
            {sections ? (
              <>
                {version && structure?.semi_finished_part_id != null && (
                  <PackingSummary
                    packing={packing}
                    semiFinishedPartId={structure.semi_finished_part_id}
                    semiFinishedNumber={structure.semi_finished_number}
                    boxes={sections.length}
                  />
                )}
                {linesTable(
                shownBox === "all" ? rows : (shownSection?.rows ?? []),
                `box-${String(shownBox)}`,
                shownBox === "all" ? t("bom.lines.empty", "No components on this revision.") : t("bom.section.empty", "Nothing in this box yet."),
                <span className="flex min-w-0 items-center gap-2">
                  {sections.length <= 6 ? (
                    <Segmented
                      value={String(shownBox)}
                      onChange={(value) => setBoxView(value === "all" ? value : Number(value))}
                      options={boxOptions}
                    />
                  ) : (
                    <Select
                      aria-label={t("column.bom.box", "Box")}
                      className="h-8 w-44"
                      value={String(shownBox)}
                      options={boxOptions}
                      onChange={(event) => {
                        const value = event.target.value;
                        setBoxView(value === "all" ? value : Number(value));
                      }}
                    />
                  )}
                  {shownBox !== "all" && (
                    <span className="hidden truncate text-[11px] text-ink-faint lg:inline">
                      {t("bom.section.box.hint", "Its carton, foam, cornières, labels and film")}
                    </span>
                  )}
                </span>,
                !readOnly && current ? (
                  <Button size="sm" variant="ghost" onClick={() => setAdding({ box: typeof shownBox === "number" ? shownBox : null })}>
                    {t("bom.add-component", "Add a component")}
                  </Button>
                ) : undefined,
              )}
                {version && typeof shownBox === "number" && (
                  <PackingPanel version={version} packing={packing} box={shownBox} boxes={sections.length} readOnly
                    productId={structure?.product_id ?? null} />
                )}
              </>
            ) : (
              linesTable(rows, "lines", t("bom.lines.empty", "No components on this revision."))
            )}
          </div>

          {routed && (
            <>
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-y border-line bg-surface px-4 py-2 lg:px-6">
              <Segmented
                value={lowerView}
                onChange={setLowerView}
                options={[
                  { value: "routing", label: t("bom.process-routing", "Routing") },
                  { value: "labour", label: t("bom.labour", "Labour standards") },
                ]}
              />
              {lowerView === "labour" && (
                <>
                  <Badge variant="neutral" size="sm">
                    {(steps ?? []).length}
                  </Badge>
                  <span className="text-[11px] text-ink-faint">
                    {t("bom.routing.total", "{{h}} h total", {
                      h: formatNumber((steps ?? []).reduce((sum, step) => sum + Number(step.hours), 0)),
                    })}
                  </span>
                </>
              )}
              <span className="text-[11px] text-ink-faint">
                {lowerView === "routing"
                  ? t("bom.process-routing.hint", "The processes the floor follows, in order, on which machines.")
                  : t("bom.labour.hint", "SIGM's labour standards per operation code, what costing reads.")}
              </span>
            </div>

            {lowerView === "routing" && current ? (
              <div className="flex min-h-40 flex-[2] flex-col overflow-auto">
                <RoutingStepsPanel partId={part.id} bom={current} readOnly={readOnly} />
              </div>
            ) : (
            <div className="flex min-h-40 flex-[2] flex-col">
              <DataTable
                data={steps ?? []}
                columns={stepCols}
                getRowId={(row) => String(row.id)}
                editor={
                  readOnly || !current
                    ? undefined
                    : {
                        fields: {
                          position: {
                            kind: "number",
                            get: (row) => row.position,
                            set: (row, value) => ({ ...row, position: requiredNumber(value) }),
                            required: true,
                          },
                          operation: {
                            kind: "select",
                            get: (row) => String(row.operation_id),
                            set: (row, value) => ({ ...row, operation_id: Number(value) }),
                            options: operationOptions,
                            required: true,
                          },
                          hours: {
                            kind: "number",
                            get: (row) => row.hours,
                            set: (row, value) => ({ ...row, hours: requiredNumber(value) }),
                          },
                          crew_size: {
                            kind: "number",
                            get: (row) => row.crew_size,
                            set: (row, value) => ({ ...row, crew_size: requiredNumber(value) }),
                          },
                          is_setup: {
                            kind: "checkbox",
                            get: (row) => row.is_setup,
                            set: (row, value) => ({ ...row, is_setup: value }),
                          },
                          department: {
                            kind: "select",
                            get: (row) => (row.department_id === null ? "" : String(row.department_id)),
                            set: (row, value) => ({ ...row, department_id: value === "" ? null : Number(value) }),
                            options: departments
                              .filter((department) => department.is_active)
                              .map((department) => ({ value: String(department.id), label: department.code })),
                          },
                        },
                        newRow: (): BomOperationDTO => ({
                          id: 0,
                          bom_id: current.id,
                          position: Math.max(0, ...(steps ?? []).map((step) => step.position)) + 10,
                          operation_id: operations.find((operation) => operation.is_active)?.id ?? 0,
                          department_id: null,
                          station_id: null,
                          is_setup: false,
                          hours: 0,
                          crew_size: 1,
                          note: null,
                        }),
                        validate: (row) =>
                          fieldErrors({
                            position: numberError(row.position, "whole", t),
                            operation: row.operation_id === 0 ? t("validation.required", "Required") : undefined,
                            hours: numberError(row.hours, "not-negative", t),
                            crew_size: numberError(row.crew_size, "positive", t),
                          }),
                        onSave: async (row, isNew) => {
                          if (isNew) await operationClient.create(row);
                          else await operationClient.update(row);
                          await refresh();
                          toast.success(t("notification.saved", "Saved"));
                        },
                        onDelete: async (row) => {
                          await operationClient.remove(row.id);
                          await refresh();
                          toast.success(t("notification.deleted", "Deleted"));
                        },
                        deleteConfirm: () => ({ title: t("bom.step.delete", "Remove this step?") }),
                        addLabel: t("button.add-step", "Add step"),
                      }
                }
                emptyMessage={t("bom.routing.empty", "No routing recorded. 8,481 imported revisions are in this state.")}
              />
            </div>
            )}
            </>
          )}
        </div>
      )}
      {adding && current && (
        <AddComponentDialog
          bomId={current.id}
          box={adding.box}
          boxes={sections ? sections.length : 0}
          nextPosition={Math.max(0, ...(lines ?? []).map((line) => line.position)) + 10}
          units={units}
          exclude={[part.id]}
          onClose={() => setAdding(null)}
          onAdded={async () => {
            setAdding(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * "Cut from P-PAR-16-BLANC-4997": the real panel a virtual panel line is cut
 * from. A select among the virtual panel's options for whoever may write,
 * saved at once; the panel's number otherwise.
 */
function CutFrom({
  row,
  options,
  editable,
  onSave,
}: {
  row: BomRow;
  options: VirtualPartOptionDTO[] | undefined;
  editable: boolean;
  onSave: (row: BomRow, realPartId: number | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  if (!editable || !options) {
    return (
      <span className="truncate text-[12px]">
        {row.default_real_part_number ?? <span className="text-ink-faint">{t("bom.virtual.no-default", "No real panel")}</span>}
      </span>
    );
  }
  const choices = options.map((option) => ({
    value: String(option.real_part_id),
    label: [option.real_part_number, option.length_mm != null && option.width_mm != null ? `${formatNumber(option.length_mm)} x ${formatNumber(option.width_mm)}` : null]
      .filter(Boolean)
      .join(" · "),
  }));
  // A default no longer among the options still shows, so the select does not lie.
  if (row.default_real_part_id != null && !options.some((option) => option.real_part_id === row.default_real_part_id)) {
    choices.unshift({ value: String(row.default_real_part_id), label: row.default_real_part_number ?? String(row.default_real_part_id) });
  }
  return (
    <span className="flex min-w-0 items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
      <span className="shrink-0 text-[11px] text-ink-faint">{t("bom.virtual.cut-from", "Cut from")}</span>
      <Select
        value={row.default_real_part_id == null ? "" : String(row.default_real_part_id)}
        options={[{ value: "", label: t("bom.virtual.no-default", "No real panel") }, ...choices]}
        disabled={saving}
        aria-label={t("bom.virtual.cut-from", "Cut from")}
        className="h-7 min-w-0 flex-1 text-[12px]"
        onChange={(event) => {
          const value = event.target.value === "" ? null : Number(event.target.value);
          setSaving(true);
          void onSave(row, value).finally(() => setSaving(false));
        }}
      />
    </span>
  );
}

/**
 * What this bill is in its product's structure (0066): the fabrication BOM of
 * a semi-finished (everything Laval makes, for every box), or the assembly BOM
 * of a version (the semi-finished and what Terrebonne adds, by box), with the
 * other parts of the structure a click away. Nothing for any other part.
 */
function BomRoleStrip({ partId, structure }: { partId: number; structure: ProductStructure | null }) {
  const { t } = useTranslation();
  if (!structure) return null;
  const version = structure.versions.find((row) => row.output_part_id === partId) ?? null;
  const semiFinished = structure.semi_finished_part_id === partId;
  if (!version && !semiFinished) return null;
  const partLink = (id: number, label: string) => (
    <Link to="/manufacturing/parts/$partId" params={{ partId: String(id) }} className="font-medium text-accent underline-offset-2 hover:underline">
      {label}
    </Link>
  );
  const product = (
    <Link to="/products/$productId" params={{ productId: String(structure.product_id) }} className="font-medium text-accent underline-offset-2 hover:underline">
      {structure.sku}
    </Link>
  );
  const others = structure.versions.filter((row) => row.output_part_id !== partId);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-surface-sunk px-4 py-2 text-[12px] lg:px-6">
      <Badge variant="info" size="sm">
        {version
          ? t("bom.role.assembly", "Assembly BOM · version {{code}}", { code: version.code })
          : t("bom.role.fabrication", "Fabrication BOM · semi-finished")}
      </Badge>
      <span className="text-ink-muted">
        {version
          ? t("bom.role.assembly.hint", "What Terrebonne boxes for {{sku}}: the semi-finished and, by box, the kit, foam, cornières and packaging ({{boxes}} boxes).", {
              sku: version.output_sku ?? structure.sku,
              boxes: version.box_count ?? 1,
            })
          : t("bom.role.fabrication.hint", "Everything Laval makes for the product, for all its boxes.")}
      </span>
      <span className="flex flex-wrap items-center gap-x-2">
        <span className="text-ink-faint">{t("bom.role.product", "Product")}</span>
        {product}
        {version && structure.semi_finished_part_id != null && (
          <>
            <span className="text-ink-faint">· {t("bom.role.semi-finished", "Semi-finished")}</span>
            {partLink(structure.semi_finished_part_id, structure.semi_finished_number ?? "")}
          </>
        )}
        {(semiFinished ? structure.versions : others).map((row) => (
          <span key={row.id} className="flex items-center gap-1">
            <span className="text-ink-faint">· {t("bom.role.version", "Version {{code}}", { code: row.code })}</span>
            {partLink(row.output_part_id, row.output_part_number ?? String(row.output_part_id))}
          </span>
        ))}
      </span>
    </div>
  );
}

/**
 * A component added to a revision: any part, how many per unit (in its stock
 * unit), and on an assembly BOM the box it goes in (none: every box).
 */
function AddComponentDialog({
  bomId,
  box,
  boxes,
  nextPosition,
  units,
  exclude,
  onClose,
  onAdded,
}: {
  bomId: number;
  box: number | null;
  /** The version's boxes; 0 on a BOM that is not an assembly BOM. */
  boxes: number;
  nextPosition: number;
  units: UnitDTO[];
  exclude: number[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [component, setComponent] = useState<PartGridDTO | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [chosenBox, setChosenBox] = useState(box == null ? "" : String(box));
  // A virtual part's quantity is the part's area or length (0078): nothing to type.
  const sized = component?.type === "VIRTUAL";
  const amount = sized ? 0 : Number(quantity);
  const add = useMutation({
    mutationFn: () =>
      new BomLineClient(http).create({
        id: 0,
        bom_id: bomId,
        position: nextPosition,
        kind: "COMPONENT",
        component_part_id: component!.id,
        quantity: amount,
        unit_id: units.find((unit) => unit.code === component!.stock_unit_code)?.id ?? null,
        operation_id: null,
        scrap_percent: 0,
        note: null,
        consumption_warehouse_id: null,
        supply: null,
        box_number: chosenBox === "" ? null : Number(chosenBox),
      }),
    onSuccess: async () => {
      toast.success(t("bom.add-component.done", "{{part}} added", { part: component?.number ?? "" }));
      await onAdded();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("bom.add-component", "Add a component")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!component || (!sized && !(amount > 0))} loading={add.isPending} onClick={() => add.mutate()}>
            {t("bom.add-component.confirm", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("column.part", "Part")} htmlFor="bom-add-part" required>
          {component ? (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="font-medium">{component.number}</span>
              <span className="text-ink-muted">{component.name}</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setComponent(null)}>
                {t("button.change", "Change")}
              </Button>
            </div>
          ) : (
            <PartPicker onPick={setComponent} exclude={exclude} autoFocus />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          {sized ? (
            <p className="text-[13px] text-ink-muted">
              {t("bom.add-component.sized", "Its quantity is the part's length and width (or length): set those on the part.")}
            </p>
          ) : (
            <Field label={t("bom.add-component.quantity", "Per unit")} htmlFor="bom-add-quantity" required>
              <span className="flex items-center gap-2">
                <Input
                  id="bom-add-quantity"
                  type="number"
                  min="0"
                  step="any"
                  value={quantity}
                  invalid={!(amount > 0)}
                  onChange={(event) => setQuantity(event.target.value)}
                />
                <span className="text-[12px] text-ink-muted">{component?.stock_unit_code ?? ""}</span>
              </span>
            </Field>
          )}
          {boxes > 0 && (
            <Field label={t("column.bom.box", "Box")} htmlFor="bom-add-box">
              <Select
                id="bom-add-box"
                value={chosenBox}
                options={[
                  { value: "", label: t("bom.section.none", "No box (the unit as a whole)") },
                  ...Array.from({ length: boxes }, (_, index) => ({
                    value: String(index + 1),
                    label: t("bom.section.box", "Box {{n}}", { n: index + 1 }),
                  })),
                ]}
                onChange={(event) => setChosenBox(event.target.value)}
              />
            </Field>
          )}
        </div>
      </div>
    </Dialog>
  );
}
